import { Link, useLocation } from "react-router-dom";

export function SidebarNav({ className = "" }: { className?: string }) {
  const { pathname } = useLocation();

  const navItems = [
    {
      to: "/today",
      label: "今日",
      sublabel: "Today",
      icon: (active: boolean) => (
        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      to: "/wordbook",
      label: "詞庫",
      sublabel: "Decks",
      icon: (active: boolean) => (
        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
          <path d="M6 6h10" />
          <path d="M6 10h10" />
        </svg>
      ),
    },
    {
      to: "/quiz/review",
      label: "練習",
      sublabel: "Practice",
      icon: (active: boolean) => (
        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
          <path d="M7 7h.01" />
        </svg>
      ),
    },
    {
      to: "/stats",
      label: "統計",
      sublabel: "Statistics",
      icon: (active: boolean) => (
        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
    },
    {
      to: "/settings",
      label: "我的",
      sublabel: "Profile",
      icon: (active: boolean) => (
        <svg className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
  ];

  return (
    <aside className={`w-[220px] shrink-0 min-h-screen border-r border-slate-100/90 bg-white/70 backdrop-blur-md flex flex-col justify-between p-5 select-none ${className}`}>
      <div>
        {/* Brand Logo & Header */}
        <Link to="/today" className="flex items-center gap-3 no-underline group mb-8 px-2">
          <div className="w-10 h-10 rounded-[14px] bg-[#2563EB] shadow-md shadow-blue-500/20 flex items-center justify-center text-white group-hover:scale-105 transition-transform">
            {/* Open book icon */}
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </div>
          <div>
            <div className="text-[16px] font-bold text-slate-900 tracking-tight leading-tight">
              單詞筆記
            </div>
            <div className="text-[11px] text-slate-400 font-sans tracking-tight">
              Build a brighter you
            </div>
          </div>
        </Link>

        {/* Navigation list */}
        <nav className="flex flex-col gap-1.5" aria-label="Main Navigation">
          {navItems.map((item) => {
            const active = pathname === item.to || (item.to !== "/today" && pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={`flex items-center gap-3.5 px-3.5 py-2.5 rounded-[14px] transition-all no-underline ${
                  active
                    ? "bg-[#EEF4FF] text-blue-600 shadow-2xs font-medium"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/60"
                }`}
              >
                <div className="shrink-0">{item.icon(active)}</div>
                <div className="flex flex-col text-left leading-tight">
                  <span className={`text-[14px] ${active ? "font-bold text-blue-600" : "font-medium text-slate-700"}`}>
                    {item.label}
                  </span>
                  <span className={`text-[11px] font-grotesk ${active ? "text-blue-500/80 font-medium" : "text-slate-400 font-normal"}`}>
                    {item.sublabel}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Help & Support */}
      <div className="pt-4 border-t border-slate-100">
        <Link
          to="/settings"
          className="flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-slate-500 hover:text-slate-800 hover:bg-slate-100/60 transition-all no-underline"
        >
          <div className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="flex flex-col text-left leading-tight">
            <span className="text-[13px] font-medium text-slate-600">幫助中心</span>
            <span className="text-[10.5px] text-slate-400 font-grotesk">Help & Support</span>
          </div>
        </Link>
      </div>
    </aside>
  );
}
